<template>
  <div class="container mx-auto p-6">
    <div class="dashboard-sticky mb-5" :class="{ 'is-scrolled': isScrolled }">
      <div class="mb-6">
        <div id="dashboard-header" class="top-actions flex justify-between items-center glass-card-lite p-4">
          <h1 class="text-2xl font-bold text-gray-900 dark:text-white">Dashboard</h1>
          <div class="flex items-center space-x-2">
            <button
              id="dashboard-refresh-btn"
              @click="refreshStats"
              :disabled="isRefreshing"
              class="btn px-4 py-2 rounded-lg transition-all flex items-center space-x-2"
              :class="{ 'opacity-50 cursor-not-allowed': isRefreshing }"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" :class="{ 'animate-spin': isRefreshing }" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/>
              </svg>
              <span>{{ isRefreshing ? 'Refreshing...' : 'Refresh' }}</span>
            </button>
            <div class="h-5 w-px bg-gray-300 dark:bg-gray-600"></div>
            <button
              type="button"
              class="btn px-4 py-2 rounded-lg transition-all flex items-center space-x-2"
              @click="startTutorial"
              title="View UI Guide"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-5 w-5 lucide lucide-circle-question-mark"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/></svg>
              <span>Guide</span>
            </button>
            <div class="h-5 w-px bg-gray-300 dark:bg-gray-600"></div>
            <button
              id="dashboard-download-btn"
              @click.stop="downloadManager"
              title="Download PDF UI Guide"
              class="btnd p-2 rounded-lg transition-all"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-arrow-down-to-line"><path d="M12 17V3"/><path d="m6 11 6 6 6-6"/><path d="M19 21H5"/></svg>
            </button>
            <div class="h-5 w-px bg-gray-300 dark:bg-gray-600"></div>
            <DashboardSelector id="dashboard-selector" class="settings-button" v-model="visibleSections" />
          </div>
        </div>
      </div>

      <!-- OrganizationTabs -->
      <OrganizationTabs
        v-if="user?.is_staff || isServiceProviderUser"
        :organizations="tenant.organizations.value"
        :selected-org-id="tenant.currentOrganizationId.value"
        :loading="tenant.isLoading.value"
        :show-tabs="true"
        loading-text="Loading dashboard..."
        @select="handleOrgSelect"
      />
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 animate-pulse">
      <div v-for="n in 8" :key="n" class="bg-surface-light dark:bg-surface-dark p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 h-32">
        <div class="flex justify-between items-start mb-4">
          <div class="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
          <div class="h-8 w-8 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
        </div>
        <div class="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/3"></div>
      </div>
    </div>

    <!-- Error State -->
    <div
      v-else-if="error"
      class="bg-red-100 text-red-800 font-semibold p-4 rounded-lg border border-red-200 flex items-center justify-center"
      role="alert"
    >
      <svg
        class="h-5 w-5 mr-2 text-red-600"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <path
          stroke="currentColor"
          stroke-width="2"
          d="M12 9v4m0 4h.01M12 5.5a7.5 7.5 0 100 15 7.5 7.5 0 000-15z"
        />
      </svg>
      Error loading dashboard: {{ error }}
    </div>

    <template v-else>
      <!-- Changes and Configuration -->
      <div v-if="visibleSections.changes && (canCommitChanges || isStaff)" class="pending-changes-section mb-8">
        <h2 class="text-xl font-bold text-gray-900 dark:text-white mb-4">Pending Changes</h2>
        <div class="grid gap-6">
          <!-- First column: Original stat cards in a 2x2 grid -->
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard v-if="hasModule('sites')"
              title="Pending Site Changes"
              :value="stats.changes_stats.configuration_pending.sites"
              :icon="GitBranch"
              :class="{ 'animate-shake': stats.changes_stats.configuration_pending.sites > 0 }"
              :link="true"
            />
            <StatCard v-if="hasModule('probes') || isStaff"
              title="Pending Probe Changes"
              :value="stats.changes_stats.configuration_pending.probes"
              :icon="GitBranch"
              :class="{ 'animate-shake': stats.changes_stats.configuration_pending.probes > 0 }"
              :link="true"
            />
            <StatCard v-if="hasModule('tunnels') || isStaff"
              title="Pending Tunnel Changes"
              :value="stats.changes_stats.configuration_pending.tunnels"
              :icon="GitBranch"
              :class="{ 'animate-shake': stats.changes_stats.configuration_pending.tunnels > 0 }"
              :link="true"
            />
            <StatCard v-if="hasModule('loadsheddingareas') || isStaff"
              title="Pending Loadshedding Area Changes"
              :value="stats.changes_stats.configuration_pending.loadshedding_areas"
              :icon="GitBranch"
              :class="{ 'animate-shake': stats.changes_stats.configuration_pending.loadshedding_areas > 0 }"
              :link="true"
            />
            <StatCard v-if="hasModule('blackboxes') || isStaff"
              title="Pending Endpoint Availability Changes"
              :value="stats.changes_stats.configuration_pending.blackboxes"
              :icon="GitBranch"
              :class="{ 'animate-shake': stats.changes_stats.configuration_pending.blackboxes > 0 }"
              :link="true"
            />
            <StatCard v-if="hasModule('hostavailability') || isStaff"
              title="Pending Host Availability Changes"
              :value="stats.changes_stats.configuration_pending.host_availability"
              :icon="GitBranch"
              :class="{ 'animate-shake': stats.changes_stats.configuration_pending.host_availability > 0 }"
              :link="true"
            />
            <StatCard v-if="hasModule('networkdevice') || isStaff"
              title="Pending Network Device Changes"
              :value="stats.changes_stats.configuration_pending.network_devices"
              :icon="GitBranch"
              :class="{ 'animate-shake': stats.changes_stats.configuration_pending.network_devices > 0 }"
              :link="true"
            />
            <StatCard v-if="hasModule('fortigates') || isStaff"
              title="Pending Fortigate Changes"
              :value="stats.changes_stats.configuration_pending.fortigates"
              :icon="GitBranch"
              :class="{ 'animate-shake': stats.changes_stats.configuration_pending.blackboxes > 0 }"
              :link="true"
            />
            <StatCard v-if="hasModule('fortigates') || isStaff"
              title="Pending Fortigate Changes"
              :value="stats.changes_stats.configuration_pending.fortigates"
              :icon="GitBranch"
               :class="{ 'animate-shake': stats.changes_stats.configuration_pending.fortigates > 0 }"
              :link="true"
            />
            <StatCard v-if="isStaff || hasModule('workspaces')"
              title="Unapproved Agents"
              :value="stats?.workspace_stats?.license_states?.unapproved?.total || 0"
              :icon="GitBranch"
              :class="{ 'animate-shake': (stats?.workspace_stats?.license_states?.unapproved?.total || 0) > 0 }"
              :link="true"
            />
            <StatCard 
              title="Config Changes Pending"
              :value="totalConfigChangesPending"
              :icon="Settings"
              :class="{ 'animate-shake': totalConfigChangesPending > 0 }"
              :link="true"
            />
          </div>
        </div>
      </div>

      <div v-if="hasModule('workspaces') || isStaff" class="license-state-section mb-8" id="license-state-section">
        <div class="flex justify-between">
          <h2 class="text-xl font-bold text-gray-900 dark:text-white mb-4">
            Agents License Status
          </h2>
        </div>
        <div :class="visibleSections.showStat ? 'grid md:grid-cols-2 gap-6' : 'grid gap-6' ">
          <div :class="visibleSections.showStat ? 'grid grid-cols-2 gap-4' : 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4'">
            <StatCard
              title="Active"
              :value="stats.workspace_stats?.license_states?.active?.total || 0"
              :icon="ClipboardCheck"

            />
            <StatCard
              title="Unapproved"
              :value="stats.workspace_stats?.license_states?.unapproved?.total || 0"
              :icon="Wrench"

              :style="{ backgroundColor: 'rgb(239, 246, 255) dark:bg-gray-800'}"
            />
            <StatCard
              title="Hardstop"
              :value="stats.workspace_stats?.license_states?.hardstop?.total || 0"
              :icon="CircleX"

              :link="true"
            />
            <StatCard
              title="Pending"
              :value="stats.workspace_stats?.license_states?.pending?.total || 0"
              :icon="Clock"

              :link="true"
            />
          </div>
          <div class="bg-white rounded-lg">
            <WorkspaceChart
              v-if="visibleSections.showStat && stats.workspace_stats?.license_states"
                :workspaceData="stats.workspace_stats"
            />
          </div>
        </div>
      </div>

      <div v-if="hasModule('workspaces') || isStaff" class="license-update-section mb-8">
        <div class="flex justify-between">
          <h2 class="text-xl font-bold text-gray-900 dark:text-white mb-4">
            Agents Update Status
          </h2>
        </div>
        <div :class="visibleSections.showStat ? 'grid md:grid-cols-2 gap-6' : 'grid gap-6' ">
          <div :class="visibleSections.showStat ? 'grid grid-cols-2 gap-4' : 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4'">
            <StatCard
              title="Queued"
              :value="stats.workspace_stats?.update_states?.queued?.total || 0"
              :icon="ListOrdered"

            />
            <StatCard
              title="In Progress"
              :value="stats.workspace_stats?.update_states?.in_progress?.total || 0"
              :icon="LoaderCircle"

              :style="{ backgroundColor: 'rgb(239, 246, 255) dark:bg-gray-800'}"
            />
            <StatCard
              title="Completed"
              :value="stats.workspace_stats?.update_states?.completed?.total || 0"
              :icon="CircleCheck"

              :link="true"
            />
            <StatCard
              title="Failed"
              :value="stats.workspace_stats?.update_states?.failed?.total || 0"
              :icon="CircleX"

              :link="true"
            />
          </div>
          <div class="bg-white rounded-lg">
            <WorkspaceUpdatesChart
              v-if="visibleSections.showStat && stats.workspace_stats?.update_states"
                :workspaceData="stats.workspace_stats"
            />
          </div>
        </div>
      </div>

      <!-- Infrastructure Status -->
      <div v-if="visibleSections.infrastructure && filteredInfrastructureStats.length > 0" class="infrastructure-section mb-8">
        <div class="flex justify-between">
          <h2 class="text-xl font-bold text-gray-900 dark:text-white mb-4">
            Infrastructure Status
          </h2>
        </div>
        <div :class="visibleSections.showStat ? 'grid md:grid-cols-2 gap-6' : 'grid gap-6' ">
          <div :class="visibleSections.showStat ? 'grid grid-cols-2 gap-4' : 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4'">
            <StatCard
              v-for="(stat, index) in filteredInfrastructureStats"
              :key="index"
              :title="stat.title"
              :value="stat.value"
              :total="stat.total"
              :icon="stat.icon"
            />
          </div>
          <InfrastructureChart
            v-if="visibleSections.showStat"
            :infrastructure-data="stats.infrastructure_stats"
          />
        </div>
      </div>

      <!-- Site Overview -->
      <div v-if="visibleSections.sites && hasModule('sites') || isStaff" class="site-section mb-8">
        <h2 class="text-xl font-bold text-gray-900 dark:text-white mb-4">Site Overview</h2>
        <div :class="visibleSections.showStat ? 'grid md:grid-cols-2 gap-6' : 'grid gap-6' ">
          <div :class="visibleSections.showStat ? 'grid grid-cols-2 gap-4' : 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4'">
            <StatCard
              title="Total Sites"
              :value="stats.site_stats?.total || 0"
              :icon="MapPin"
              
            />
            <StatCard
              title="In Maintenance"
              :value="stats.site_stats.by_status.in_maintenance"
              :icon="Wrench"
              
              :style="{ backgroundColor: 'rgb(239, 246, 255) dark:bg-gray-800'}"
            />
            <StatCard
              title="Pending Changes"
              :value="stats.site_stats.by_status.pending"
              :icon="Clock"
              
              :link="true"
            />
            <StatCard
              title="Recent Changes"
              :value="stats.site_stats.recent_changes"
              :icon="GitBranch"
              
              :link="true"
            />
          </div>
          <div class="bg-white rounded-lg">
            <SitesChart 
            v-if="visibleSections.showStat"
            :site-data="stats.site_stats" 
            />
          </div>
        </div>

        <div :class="visibleSections.showStat ? 'grid md:grid-cols-2 gap-6 mt-6' : 'grid gap-6 mt-6'">
          <div class="p-6 glass-card">
            <h3 class="text-lg font-semibold mb-4 dark:text-white">
              Top Organisations by Site Count
            </h3>
            <div class="space-y-2">
              <div
                v-for="item in stats.site_stats.distribution.by_org"
                :key="item.org__name"
                class="flex items-center justify-between"
              >
                <span class="text-gray-600 dark:text-white">{{
                  tenant.getOrganizationName(item.org) || "No Organization"
                }}</span>
                <span class="text-gray-900 dark:text-white font-medium">{{
                  item.site_count
                }}</span>
              </div>
            </div>
          </div>
          <DivisionBarChart
            v-if="visibleSections.showStat"
            :division-data="stats.site_stats.by_division" 
          />
        </div>
      </div>

      <!-- Probe Overview -->
      <div v-if="visibleSections.probes && hasModule('probes') || isStaff" class="probe-section mb-8">
        <h2 class="text-xl font-bold text-gray-900 dark:text-white mb-4">Probe Overview</h2>
        <div :class="visibleSections.showStat ? 'grid md:grid-cols-2 gap-6' : 'grid gap-6' ">
          <div :class="visibleSections.showStat ? 'grid grid-cols-2 gap-4' : 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4'">
            <StatCard
              title="Total Probes"
              :value="stats.probe_stats?.total || 0"
              :icon="Wifi"
              
            />
            <StatCard
              title="In Maintenance"
              :value="stats.probe_stats.by_status.in_maintenance"
              :icon="Wrench"
              class="p-6 rounded-lg shadow-sm border text-blue-600"
              :style="{ backgroundColor: 'rgb(239, 246, 255) dark:bg-gray-800' }"
            />
            <StatCard
              title="Pending Changes"
              :value="stats.probe_stats.by_status.pending"
              :icon="Clock"
              :link="true"
            />
            <StatCard
              title="Installed"
              :value="stats.probe_stats.by_status.is_installed"
              :icon="CheckCircle"
            />
          </div>
          <ProbeOperationalChart 
            v-if="visibleSections.showStat"
            :probe-data="stats.probe_stats" 
          />
        </div>

        <div :class="visibleSections.showStat ? 'grid md:grid-cols-2 gap-6 mt-6' : 'grid gap-6 mt-6'">
          <div class="p-6 glass-card">
            <h3 class="text-lg font-semibold mb-4">
              Top Organisations by Probe Count
            </h3>
            <div class="space-y-2">
              <div
                v-for="item in stats.probe_stats.distribution.by_org"
                :key="item.org__name"
                class="flex items-center justify-between"
              >
                <span class="text-gray-600 dark:text-white">{{
                  tenant.getOrganizationName(item.org) || "No Organization"
                }}</span>
                <span class="text-gray-900 dark:text-white font-medium">{{
                  item.probe_count
                }}</span>
              </div>
            </div>
            <div class="mt-4 pt-4 border-t border-gray-200">
              <div class="flex items-center justify-between">
                <span class="text-gray-600 dark:text-white">Recent Changes (24h)</span>
                <span class="text-gray-900 dark:text-white font-medium">
                  {{ stats.probe_stats.recent_changes }}
                </span>
              </div>
            </div>
          </div>
          <ProbeAuthChart
            v-if="visibleSections.showStat"
           :probe-data="stats.probe_stats" />
        </div>
      </div>

      <!-- Tunnel Overview -->
      <div v-if="visibleSections.tunnels && hasModule('tunnels') || isStaff" class="tunnel-section mb-8">
        <h2 class="text-xl font-bold text-gray-900 dark:text-white mb-4">Tunnel Overview</h2>
        <div class="grid grid-cols-1 md:grid-cols-4 gap-6">
          <StatCard
            title="Total Tunnels"
            :value="stats.tunnel_stats?.total || 0"
            :icon="Link"
            
          />
          <StatCard
            title="Sites with Tunnels"
            :value="Object.keys(stats.tunnel_stats.by_site).length"
            :icon="Network"
            
          />
          <StatCard
            title="Pending Changes"
            :value="stats.tunnel_stats.by_status.pending"
            :icon="Clock"
            :link="true"
          />
          <StatCard
            title="Recent Changes"
            :value="stats.tunnel_stats.recent_changes"
            :icon="GitBranch"
          />
        </div>
      </div>

      <!-- Division Overview -->
      <div  class="division-section mb-8" v-if="visibleSections.divisions && hasModule('divisions') || isStaff">
        <h2 class="text-xl font-bold text-gray-900 dark:text-white mb-4">Division Overview</h2>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
          <StatCard
            title="Total Divisions"
            :value="stats.division_stats?.total || 0"
            :icon="GitFork"
            
          />
          <StatCard
            title="Core Templates"
            :value="stats.division_stats.templates.core"
            :icon="FileText"
            
          />
          <StatCard
            title="Edge Templates"
            :value="stats.division_stats.templates.edge"
            :icon="FileCode"
            
          />
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
          <div class="bg-white p-6 dark:bg-gray-800 dark:border-gray-800 rounded-lg shadow-sm border">
            <h3 class="text-lg font-semibold mb-4">
              Divisions by Organization
            </h3>
            <div class="space-y-2">
              <div
                v-for="(item, index) in stats.division_stats.by_org_details"
                :key="index"
                class="flex items-center justify-between"
              >
                <span class="text-gray-600 dark:text-white">{{
                  tenant.getOrganizationName(item.org) || "No Organization"
                }}</span>
                <span class="text-gray-900 dark:text-white font-medium">{{
                  item.division_count
                }}</span>
              </div>
            </div>
          </div>

          <div class="bg-white p-6 dark:bg-gray-800 dark:border-gray-800 rounded-lg shadow-sm border">
            <h3 class="text-lg font-semibold mb-4">Division Status</h3>
            <div class="space-y-4">
              <div class="flex items-center justify-between">
                <span class="text-gray-600 dark:text-white">Recent Changes (24h)</span>
                <span class="text-gray-900 dark:text-white font-medium">
                  {{ stats.division_stats.recent_changes }}
                </span>
              </div>
              <div class="flex items-center justify-between">
                <span class="text-gray-600 dark:text-white">With Core Templates</span>
                <span class="text-gray-900 dark:text-white font-medium">
                  {{ stats.division_stats.templates.core }}
                </span>
              </div>
              <div class="flex items-center justify-between">
                <span class="text-gray-600 dark:text-white">With Edge Templates</span>
                <span class="text-gray-900 dark:text-white font-medium">
                  {{ stats.division_stats.templates.edge }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Security Overview -->
      <div v-if="visibleSections.security && isSuperUser" class="mb-8">
        <h2 class="text-xl font-bold text-gray-900 dark:text-white mb-4">Security Overview</h2>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
          <StatCard
            title="Access Attempts (24h)"
            :value="stats.security_stats.access_attempts.last_24h"
            :icon="Shield"
            :trend="
              calculateTrend(
                stats.security_stats.access_attempts.last_24h,
                stats.security_stats.access_attempts.last_7d / 7
              )
            "
            
          />
          <StatCard
            title="Failed Attempts"
            :value="stats.security_stats.access_attempts.failed_attempts"
            :icon="AlertTriangle"
          />
          <StatCard
            title="Access Logs (24h)"
            :value="stats.security_stats.access_logs.last_24h"
            :icon="Clock"
            
          />
        </div>
      </div>

      <!-- Recent Activity and Failed Attempts -->
      <div
        v-if="visibleSections.activity && isSuperUser"
        class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8"
      >
        <div class="glass-card p-6">
          <h2 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Top Failed Access Attempts by IP
          </h2>
          <div v-if="stats.security_stats.top_failed_ips.length > 0" class="space-y-4">
            <div
              v-for="ip in stats.security_stats.top_failed_ips"
              :key="ip.ip_address"
              class="flex items-center justify-between"
            >
              <span class="text-gray-600 dark:text-white">{{ ip.ip_address }}</span>
              <span class="text-gray-900 dark:text-white font-medium"
                >{{ ip.count }} attempts</span
              >
            </div>
          </div>
          <div v-else class="text-center text-gray-500 dark:text-gray-400 py-4">
            No failed attempts recorded.
          </div>
        </div>

        <div v-if="isSuperUser" class="glass-card p-6">
          <h2 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Recent Activity
          </h2>
          <div v-if="stats.recent_stats.recent_activity.length > 0" class="space-y-4">
            <div
              v-for="(activity, index) in stats.recent_stats.recent_activity"
              :key="index"
              class="flex flex-col space-y-1"
            >
              <span class="text-sm text-gray-500 dark:text-white">
                {{ formatDateTime(activity.timestamp) }}
              </span>
              <p class="text-gray-900 dark:text-white">
                {{ activity.user__username }} {{ activity.action }}
                {{ activity.object_repr }}
              </p>
            </div>
          </div>
          <div v-else class="text-center text-gray-500 dark:text-gray-400 py-4">
            No recent activity.
          </div>
        </div>
      </div>

      <!-- API and Audit Stats -->
      <div v-if="visibleSections.apiAudit && isSuperUser" class="mb-8">
        <h2 class="text-xl font-bold text-gray-900 dark:text-white mb-4">
          System Activity (30 Days)
        </h2>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div class="glass-card p-6">
            <h3 class="text-lg font-semibold mb-4">API Status Distribution</h3>
            <div class="space-y-2">
              <div
                v-for="(count, status) in stats.activity_stats.api_logs
                  .status_distribution"
                :key="status"
                class="flex items-center justify-between"
              >
                <span class="text-gray-600 dark:text-white">Status {{ status }}</span>
                <span class="text-gray-900 dark:text-white font-medium">{{ count }}</span>
              </div>
            </div>
          </div>

          <div class="glass-card p-6">
            <h3 class="text-lg font-semibold mb-4">Top Audit Actions</h3>
            <div class="space-y-2">
              <div
                v-for="(count, action) in stats.activity_stats.audit_logs
                  .top_actions"
                :key="action"
                class="flex items-center justify-between"
              >
                <span class="text-gray-600 dark:text-white">{{ action }}</span>
                <span class="text-gray-900 dark:text-white font-medium">{{ count }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { ref, watch, onMounted, onBeforeMount, onBeforeUnmount, computed, nextTick } from "vue";
import StatCard from "@/components/StatCard.vue";
import { getDashboardStats } from "@/services/dashboardService";
import DivisionBarChart from "@/components/charts/SitesByDivisionChart.vue";
import PendingChangesChart from "@/components/charts/PendingChangesChart.vue";
import InfrastructureChart from "@/components/charts/InfrastructureChart.vue";
import WorkspaceChart from "@/components/charts/WorkspaceAgentsChart.vue";
import WorkspaceUpdatesChart from "@/components/charts/WorkspaceUpdatesChart.vue";
import SitesChart from "@/components/charts/SitesChart.vue";
import ProbeOperationalChart from "@/components/charts/ProbeOperationalChart.vue";
import ProbeAuthChart from "@/components/charts/ProbeAuthChart.vue";
import DashboardSelector from "@/components/DashboardSelector.vue";
import {
  Shield,
  AlertTriangle,
  Clock,
  MapPin,
  Wifi,
  Link,
  Box,
  Server,
  CheckCircle,
  Wrench,
  GitBranch,
  Network,
  GitFork,
  FileText,
  FileCode,
  Settings,
  ClipboardCheck,
  CircleX,
  ListOrdered,
  LoaderCircle,
  CircleCheck,
} from "lucide-vue-next";
import { getAllModules } from "@/services/moduleService";
import { useStore } from "@/store";
import { getWorkspaceStats } from "@/services/workspaceDashboardService";
import Cookies from "js-cookie";
import { useTutorial } from "@/composables/tutorial";
import { useMultiTenant } from "@/composables/useMultiTenant";
import OrganizationTabs from "@/components/OrganizationTabs.vue";

export default {
  data() {
    return {
      showChart: false, // Initially set to false to show numbers  
    };
  },
  name: "DashboardView",
  components: {
    StatCard,
    Shield,
    AlertTriangle,
    Clock,
    MapPin,
    Wifi,
    Link,
    Box,
    CheckCircle,
    Wrench,
    GitBranch,
    Network,
    GitFork,
    FileText,
    FileCode,
    Settings,
    ClipboardCheck,
    CircleX,
    ListOrdered,
    LoaderCircle,
    CircleCheck,
    DivisionBarChart,
    InfrastructureChart,
    SitesChart,
    ProbeOperationalChart,
    ProbeAuthChart,
    DashboardSelector,
    WorkspaceChart,
    WorkspaceUpdatesChart,
    OrganizationTabs,
  },
  setup() {
    const tenant = useMultiTenant();
    const stats = ref({
      security_stats: {
        access_attempts: { last_24h: 0, failed_attempts: 0, last_7d: 0 },
        access_logs: { last_24h: 0 },
        top_failed_ips: [],
      },
      infrastructure_stats: {
        sites: { active: 0, total: 0 },
        probes: { active: 0, total: 0 },
        tunnels: { active: 0, total: 0 },
        blackboxes: { total: 0 },
        host_availability: { total: 0 },
        network_devices: { total: 0 },
      },
      site_stats: {
        total: 0,
        by_status: { in_maintenance: 0, pending: 0 },
        recent_changes: 0,
        distribution: { by_org: [] },
        by_division: {},
      },
      probe_stats: {
        total: 0,
        by_status: {
          is_installed: 0,
          pending: 0,
          in_maintenance: 0,
          is_approved: 0,
          no_api: 0,
        },
        recent_changes: 0,
        distribution: { by_org: [] },
      },
      tunnel_stats: {
        total: 0,
        by_status: { pending: 0 },
        recent_changes: 0,
        by_site: {},
        distribution: { by_org: [] },
      },
      division_stats: {
        total: 0,
        templates: { core: 0, edge: 0 },
        recent_changes: 0,
        by_org_details: [],
      },
      changes_stats: {
        pending_changes: { sites: 0, probes: 0 },
        configuration_pending: { sites: 0, probes: 0, tunnels: 0, fortigates: 0, blackboxes: 0, loadshedding_areas: 0, host_availability: 0, network_devices: 0 },
      },
      activity_stats: {
        api_logs: { status_distribution: {} },
        audit_logs: { top_actions: {} },
      },
      recent_stats: {
        recent_activity: [],
      },
      workspace_stats: {
        agent_stats:  {total : 0},
        license_states: {
          active: { total: 0},
          unapproved: { total: 0},
          hardstop: { total: 0},
          pending: {  total:  0},
        },
        update_states: {
          failed: { total: 0},
          completed: { total: 0},
          in_progress: { total: 0},
          queued: {  total:  0},
          overall: 0
        }
      }
    });

    const isRefreshing = ref(false);
    const orgModules = ref({});
    const isModulesLoaded = ref(false)
    const isScrolled = ref(false);
    const isTutorialActive = ref(false);

    const handleScroll = () => {
      if (isTutorialActive.value) return;
      isScrolled.value = window.scrollY > 8;
    };
    const user = computed(() => useStore.getUser());
    const isServiceProviderUser = computed(() => useStore.isServiceProviderUser());

    const isLoading = ref(true);
    const error = ref(null);
    const superuserModules = ref([
      'blackboxes',
      'probes',
      'sites',
      'tunnels',
      'loadsheddingareas',
      'fortigates',
      'hostavailability',
      'networkdevice',
    ]);

    const handleOrgSelect = async (orgId) => {
      try {
        await tenant.switchOrganization(orgId, async () => {
          await fetchStats();
          await fetchWorkspaceStats();
        });
      } catch (error) {
        console.error("Failed to switch organization:", error);
        error.value = "Failed to switch organization";
      }
    };

    const fetchModules = async() => {
      try {
        
        const currentUser = user.value;

        if (!currentUser) {
          return;
        }

        if (currentUser?.is_staff){
          orgModules.value = new Set(superuserModules.value);

        } else{
          const allmodules = await getAllModules();
          const modules = allmodules
          .filter(module => module.uiconfig === currentUser?.org && module.enabled)
          .map(({ name }) => {
            const lower = name.toLowerCase();
            if (lower === 'blackbox') return 'blackboxes';
            if (lower === 'hostavailability') return 'hostavailability';
            if (lower === 'networkdevice') return 'networkdevice';
            return lower + 's';
          });

          orgModules.value = new Set(modules);
        }

        isModulesLoaded.value = true;  

      } catch (error) {
        console.error(error)
      }
    }; 


  const downloadPdf = (filename) => {
    const link = document.createElement('a');
    link.href = `/howto/${filename}`;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };


  const PDF_FILES = {
    config: 'New_Mivu_UI_Guide.pdf',
    workspace: 'New_Mivu_Work_Space_Guide.pdf'
  };


  const downloadManager = () => {
    const modules = orgModules.value;
    const hasWorkspaces = modules.has("workspaces");
    const hasConfigs = modules.size > 0 && !hasWorkspaces;
   
    if (isStaff.value || modules.size === 0) {
      downloadPdf(PDF_FILES.config);
      downloadPdf(PDF_FILES.workspace);
      return;
    }
    
    if (hasWorkspaces) {
      downloadPdf(PDF_FILES.workspace);
    }
    if (hasConfigs) {
      downloadPdf(PDF_FILES.config);
    }
  };

    const hasModule = computed(() => {
      return (module) => {
        return isModulesLoaded.value && orgModules.value.has(module);
      };
    });

    const isSuperUser = computed(() => {
      return user.value?.is_superuser;
    });

    const isStaff = computed(() => {
      return user.value?.is_staff;
    });

    const userOrg = computed(() => {
      return useStore.userOrg();
    });


    const canCommitChanges = computed(() => {
      return useStore?.hasPermission("Can commit changes to production");
    });

    const infrastructureStats = computed(() => [
      {
        title: "Active Sites",
        module: "sites",
        value:
          Number(stats.value.site_stats?.total || 0) -
          Number(stats.value.site_stats?.by_status?.in_maintenance || 0),
        total: Number(stats.value.site_stats?.total || 0),
        icon: MapPin,
      },
      {
        title: "Active Probes",
        module: "probes",
        value: Number(stats.value.probe_stats?.by_status?.is_installed || 0),
        total: Number(stats.value.probe_stats?.total || 0),
        icon: Wifi,
      },
      {
        title: "Active Tunnels",
        module: "tunnels",
        value:
          Number(stats.value.tunnel_stats?.total || 0) -
          Number(stats.value.tunnel_stats?.by_status?.pending || 0),
        total: Number(stats.value.tunnel_stats?.total || 0),
        icon: Link,
      },
      {
        title: "Endpoint Availability",
        module: "blackboxes",
        value: Number(stats.value.infrastructure_stats?.blackboxes?.total || 0),
        icon: Box,
      },
      {
        title: "Host Availability",
        module: "hostavailability",
        value: Number(stats.value.infrastructure_stats?.host_availability?.total || 0),
        icon: Server,
      },
      {
        title: "Network Devices",
        module: "networkdevice",
        value: Number(stats.value.infrastructure_stats?.network_devices?.total || 0),
        icon: Server,
      },
    ]);

    const filteredInfrastructureStats = computed(() => {
      return infrastructureStats.value.filter((mod) => hasModule.value(mod.module))
    });

    const totalConfigChangesPending = computed(() => {
      const config = stats.value.changes_stats?.configuration_pending || {};
      return (
        (Number(config.sites) || 0) +
        (Number(config.probes) || 0) +
        (Number(config.tunnels) || 0) +
        (Number(config.loadshedding_areas) || 0) +
        (Number(config.blackboxes) || 0) +
        (Number(config.host_availability) || 0) +
        (Number(config.network_devices) || 0) +
        (stats.value.workspace_stats?.license_states?.unapproved?.total || 0)
      );
    });

    const topSitesByTunnelCount = computed(() => {
      const bySite = stats.value.tunnel_stats?.by_site || {};
      return Object.entries(bySite)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 5)
        .reduce((obj, [key, value]) => ({ ...obj, [key]: value }), {});
    });

    const siteStats = computed(() => ({
      total: Number(stats.value.site_stats?.total || 0),
      inMaintenance: Number(
        stats.value.site_stats?.by_status?.in_maintenance || 0
      ),
      pending: Number(stats.value.site_stats?.by_status?.pending || 0),
      recentChanges: Number(stats.value.site_stats?.recent_changes || 0),
    }));

    const probeStats = computed(() => ({
      total: Number(stats.value.probe_stats?.total || 0),
      installed: Number(stats.value.probe_stats?.by_status?.is_installed || 0),
      pending: Number(stats.value.probe_stats?.by_status?.pending || 0),
      inMaintenance: Number(
        stats.value.probe_stats?.by_status?.in_maintenance || 0
      ),
      approved: Number(stats.value.probe_stats?.by_status?.is_approved || 0),
      noApi: Number(stats.value.probe_stats?.by_status?.no_api || 0),
      recentChanges: Number(stats.value.probe_stats?.recent_changes || 0),
    }));

    const tunnelStats = computed(() => ({
      total: Number(stats.value.tunnel_stats?.total || 0),
      pending: Number(stats.value.tunnel_stats?.by_status?.pending || 0),
      recentChanges: Number(stats.value.tunnel_stats?.recent_changes || 0),
      sitesWithTunnels: Object.keys(stats.value.tunnel_stats?.by_site || {})
        .length,
    }));

    const divisionStats = computed(() => ({
      total: Number(stats.value.division_stats?.total || 0),
      coreTemplates: Number(stats.value.division_stats?.templates?.core || 0),
      edgeTemplates: Number(stats.value.division_stats?.templates?.edge || 0),
      recentChanges: Number(stats.value.division_stats?.recent_changes || 0),
    }));

    const securityStats = computed(() => ({
      accessAttempts24h: Number(
        stats.value.security_stats?.access_attempts?.last_24h || 0
      ),
      failedAttempts: Number(
        stats.value.security_stats?.access_attempts?.failed_attempts || 0
      ),
      accessLogs24h: Number(
        stats.value.security_stats?.access_logs?.last_24h || 0
      ),
      last7dDaily:
        Number(stats.value.security_stats?.access_attempts?.last_7d || 0) / 7,
    }));

    const calculateTrend = (current, previous) => {
      if (!previous) return 0;
      return Math.round(((current - previous) / previous) * 100);
    };

    const formatDateTime = (dateString) => {
      if (!dateString) return "";
      return new Date(dateString).toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    };

    const fetchWorkspaceStats = async () => {
      try {
        const response = await getWorkspaceStats();
        stats.value = {...stats.value, workspace_stats: response || 0}
      } catch (error) {
        console.error(error);
      }
    };
    
    // Recursive deep-merge: server values override defaults, but nested keys
    // that the server omits keep their default shape. Arrays are replaced
    // wholesale (not merged element-by-element). Null server values are
    // treated as "not set" and the default survives, so a server that returns
    // workspace_stats: null does not wipe the nested stat shape the template
    // reads directly (e.g. stats.workspace_stats.license_states.unapproved.total).
    const _isPlainObject = (v) =>
      v !== null && typeof v === "object" && !Array.isArray(v);
    const _deepMerge = (target, source) => {
      if (!_isPlainObject(source)) return target;
      const out = { ...target };
      for (const key of Object.keys(source)) {
        const s = source[key];
        const t = target?.[key];
        if (s === null || s === undefined) continue;
        if (_isPlainObject(t) && _isPlainObject(s)) {
          out[key] = _deepMerge(t, s);
        } else {
          out[key] = s;
        }
      }
      return out;
    };

    const fetchStats = async () => {
      isLoading.value = true;
      error.value = null;
      try {
        const data = await getDashboardStats(tenant.currentSchemaName.value);
        stats.value = _deepMerge(stats.value, data || {});
      } catch (err) {
        error.value = err.message;
        console.error("Error fetching dashboard stats:", err);
      } finally {
        isLoading.value = false;
      }
    };

    const refreshStats = async () =>{
      isRefreshing.value = true;
      await fetchStats();
      await fetchWorkspaceStats()
      isRefreshing.value = false;
    };


    const visibleSections = ref({
      changes: true,
      infrastructure: true,
      sites: true,
      probes: true,
      tunnels: true,
      divisions: true,
      security: true,
      activity: true,
      apiAudit: true,
      showStat: false,
      workspace_stats: true
    });

    const updateVisibleSections = (sections) => {
      visibleSections.value = sections;
    };

  
    const COOKIE_NAME = "dashboard-preferences";
    const HOWTO_COOKIE_NAME = "dashboard-howto-seen";
    const COOKIE_EXPIRY = 365;

    const { start: _start } = useTutorial();

    const dashboardTutorialSteps = [
      { element: '#main-navigation', action: 'display', message: 'Use the main navigation menu to move between sections of the application.' },
      { element: '#dashboard-header', action: 'display', message: 'Welcome to the MIVU dashboard! This is your central overview of all infrastructure.' },
      { element: '#dashboard-refresh-btn', action: 'display', message: 'Click Refresh to fetch the latest statistics from the server.' },
      { element: '#dashboard-download-btn', action: 'display', message: 'Download the PDF UI guide for detailed usage instructions.' },
      { element: '#dashboard-selector', action: 'display', message: 'Use the dashboard selector to toggle which sections are visible.' },
      { element: '.pending-changes-section', action: 'display', message: 'Pending Changes shows configuration items awaiting approval or deployment.' },
      { element: '.infrastructure-section', action: 'display', message: 'Infrastructure Status gives an at-a-glance view of active sites, probes, tunnels, and endpoints.' },
      { element: '.site-section', action: 'display', message: 'Site Overview shows statistics and regional distribution for all monitored sites.' },
      { element: '.probe-section', action: 'display', message: 'Probe Overview tracks probe health, installation status, and pending changes.' },
      { element: '.tunnel-section', action: 'display', message: 'Tunnel Overview lists all VPN tunnels and their current states.' },
      { element: '.division-section', action: 'display', message: 'Division Overview shows template distribution across your organisation\'s divisions.' },
    ];

    const startTutorial = async () => {
      isTutorialActive.value = true;
      const started = await _start(dashboardTutorialSteps, () => {
        Cookies.set(HOWTO_COOKIE_NAME, 'true', { expires: COOKIE_EXPIRY });
        isTutorialActive.value = false;
      });
      if (!started) {
        isTutorialActive.value = false;
      }
    };

    onMounted(async () => {
      const seen = Cookies.get(HOWTO_COOKIE_NAME) === 'true';
      window.addEventListener('scroll', handleScroll, { passive: true });
      handleScroll();
      await tenant.fetchOrganizations();
      await tenant.loadInitialData(async () => {
        await tenant.syncUrlParam();
        await fetchStats();
        await fetchWorkspaceStats();
      });
      if (!seen) {
        await nextTick();
        startTutorial();
      }
    });

    onBeforeUnmount(() => {
      window.removeEventListener('scroll', handleScroll);
    });

    onBeforeMount(fetchModules);

    watch(
      () => user.value,
      (newVal) => {
        if (newVal) {
          fetchModules();
        }
      },
      { immediate: true }
    );

    watch(
      () => userOrg.value,
      (newVal) => {
        if (newVal) {
         fetchWorkspaceStats();
        }
      },
      { immediate: true }
    );


    watch(
      () => tenant.currentSchemaName.value,
      (newSchema) => {
        if (newSchema) {
          fetchStats();
          fetchWorkspaceStats();
        }
      }
    );

    return {
      tenant,
      stats,
      isLoading,
      error,
      user,
      isServiceProviderUser,
      filteredInfrastructureStats,
      totalConfigChangesPending,
      topSitesByTunnelCount,
      siteStats,
      probeStats,
      tunnelStats,
      divisionStats,
      securityStats,
      calculateTrend,
      formatDateTime,
      fetchStats,
      Shield,
      AlertTriangle,
      Clock,
      MapPin,
      Wifi,
      Link,
      Box,
      CheckCircle,
      Wrench,
      GitBranch,
      Network,
      GitFork,
      FileText,
      FileCode,
      Settings,
      ClipboardCheck,
      CircleX,
      ListOrdered,
      LoaderCircle,
      CircleCheck,
      visibleSections,
      updateVisibleSections,
      hasModule,
      isSuperUser,
      canCommitChanges,
      isStaff,
      refreshStats,
      startTutorial,
      downloadManager,
      isRefreshing,
      handleOrgSelect,
      isScrolled
    };
  },
};
</script>

<style>
.dashboard-sticky {
  position: sticky;
  top: 4.5rem;
  z-index: 40;
  margin-top: -2rem;
  padding-top: 1.25rem;
  padding-bottom: 1.25rem;
  margin-bottom: 1.25rem;
  transition: box-shadow 0.3s ease;
}

.dashboard-sticky::before {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  left: 50%;
  /* Tucked up behind the nav and hidden until scrolled. */
  transform: translateX(-50%) translateY(-100%);
  transform-origin: top center;
  opacity: 0;
  width: 100vw;
  z-index: -1;
  background-color: transparent;
  border-bottom: 1px solid transparent;
  pointer-events: none;
  transition: transform 0.7s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.6s ease,
    background-color 0.6s ease, backdrop-filter 0.6s ease, border-color 0.6s ease, box-shadow 0.6s ease;
}

.dashboard-sticky.is-scrolled::before {
  @apply bg-white/95 dark:bg-surface-dark/95 backdrop-blur-md border-gray-200/70 dark:border-white/10;
  transform: translateX(-50%) translateY(0);
  opacity: 1;
  box-shadow: 0 6px 24px -8px rgba(31, 38, 135, 0.25);
}

</style>
